import { useEffect, useState } from 'react';

interface Tenant {
  id: string;
  name: string;
  username?: string;
  email?: string;
  role?: string;
  created_at: string;
  plan_name?: string;
  plan_id?: string;
  tokens_used?: number;
  tokens_saved?: number;
  token_quota?: number;
}

const CustomPlanDropdown = ({ currentPlanId, onAssign }: { currentPlanId: string, onAssign: (planId: string) => void }) => {
  const [open, setOpen] = useState(false);
  const plans = [
    { id: 'free', label: 'Starter (+500K tok)' },
    { id: 'pro', label: 'Pro (+50M tok)' },
    { id: 'team', label: 'Team (+500M tok)' }
  ];
  
  const currentPlan = plans.find(p => p.id === currentPlanId);

  return (
    <div style={{ position: 'relative' }}>
      <div 
        onClick={() => setOpen(!open)}
        style={{ padding: '6px 12px', borderRadius: '4px', background: 'var(--bg-glass)', color: 'var(--text-primary)', border: '1px solid var(--border)', fontSize: '12px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', minWidth: '160px' }}
      >
        <span>{currentPlan ? currentPlan.label : 'Assign Plan (Add Credit)'}</span>
        <span style={{ fontSize: '10px', marginLeft: '8px' }}>▼</span>
      </div>
      
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, width: '100%', background: '#1e1e24', border: '1px solid var(--border)', borderRadius: '4px', marginTop: '4px', zIndex: 10, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
          {plans.map(p => (
            <div 
              key={p.id}
              onClick={() => { onAssign(p.id); setOpen(false); }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--accent-cyan)'; e.currentTarget.style.color = '#000'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#fff'; }}
              style={{ padding: '8px 12px', fontSize: '12px', color: '#fff', cursor: 'pointer', transition: 'background 0.2s, color 0.2s' }}
            >
              {p.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const AdminTenantsPage = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);

  // Form states
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState('user');

  const fetchTenants = () => {
    fetch('https://mec.wowerix.com/v1/admin/tenants', {
      headers: { 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` }
    })
      .then(r => r.json())
      .then(data => {
        if (Array.isArray(data)) {
          setTenants(data);
        } else {
          console.error("Failed to load tenants:", data);
          setTenants([]);
        }
        setLoading(false);
      })
      .catch(err => {
        console.error("Fetch error:", err);
        setTenants([]);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchTenants();
  }, []);

  const handleCreateUser = async () => {
    if (!newUsername || !newName) return alert('Username và Display Name là bắt buộc!');
    
    await fetch('https://mec.wowerix.com/v1/admin/tenants', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('eric_token')}`
      },
      body: JSON.stringify({ 
        username: newUsername,
        password: newPassword,
        name: newName,
        email: newEmail,
        role: newRole
      })
    });
    // Reset form
    setNewUsername(''); setNewPassword(''); setNewName(''); setNewEmail(''); setNewRole('user');
    fetchTenants();
  };

  const handleDeleteUser = async (id: string) => {
    if (!confirm('Bạn có chắc chắn muốn xoá người dùng này?')) return;
    
    await fetch(`https://mec.wowerix.com/v1/admin/tenants/${id}`, {
      method: 'DELETE',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('eric_token')}` }
    });
    fetchTenants();
  };

  const handleAssignPlan = async (id: string, plan_id: string) => {
    await fetch(`https://mec.wowerix.com/v1/admin/tenants/${id}`, {
      method: 'PUT',
      headers: { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${localStorage.getItem('eric_token')}`
      },
      body: JSON.stringify({ plan_id })
    });
    fetchTenants();
  };

  if (loading) return <div>Loading tenants...</div>;

  return (
    <div className="fade-in">
      <div style={{ marginBottom: '24px' }}>
        <h2 style={{ fontSize: '24px', fontWeight: 600, color: 'var(--text-primary)' }}>Users & Tenants</h2>
        <p style={{ color: 'var(--text-secondary)', marginTop: '8px' }}>Manage all registered users and their billing usage.</p>
      </div>

      {/* CREATE USER FORM */}
      <div className="glass-panel" style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Username</label>
          <input type="text" value={newUsername} onChange={e => setNewUsername(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: '#fff' }} />
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Password</label>
          <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: '#fff' }} />
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Display Name</label>
          <input type="text" value={newName} onChange={e => setNewName(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: '#fff' }} />
        </div>
        <div style={{ flex: '1 1 150px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Email</label>
          <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: '#fff' }} />
        </div>
        <div style={{ flex: '0 0 100px' }}>
          <label style={{ display: 'block', fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '8px' }}>Role</label>
          <select value={newRole} onChange={e => setNewRole(e.target.value)} style={{ width: '100%', padding: '10px', borderRadius: '6px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', color: '#fff' }}>
            <option value="user">User</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <button onClick={handleCreateUser} style={{ padding: '10px 24px', borderRadius: '6px', background: 'var(--accent-cyan)', color: '#000', border: 'none', cursor: 'pointer', fontWeight: 600, height: '40px' }}>
          Create
        </button>
      </div>

      <div className="glass-panel" style={{ padding: '0', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)', background: 'rgba(0,0,0,0.2)' }}>
              <th style={{ padding: '16px', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>USER</th>
              <th style={{ padding: '16px', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>ROLE</th>
              <th style={{ padding: '16px', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>CURRENT PLAN</th>
              <th style={{ padding: '16px', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>TOKEN BALANCE</th>
              <th style={{ padding: '16px', fontSize: '12px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase' }}>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {tenants.map(t => (
              <tr key={t.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                <td style={{ padding: '16px' }}>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{t.name}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    {t.username || 'N/A'} &bull; {t.email || 'N/A'}
                  </div>
                </td>
                <td style={{ padding: '16px' }}>
                  <span style={{ 
                    padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                    background: t.role === 'admin' ? 'rgba(168, 85, 247, 0.2)' : 'rgba(14, 165, 233, 0.2)',
                    color: t.role === 'admin' ? '#a855f7' : '#0ea5e9'
                  }}>
                    {t.role || 'user'}
                  </span>
                </td>
                <td style={{ padding: '16px' }}>
                  <div style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{t.plan_name || 'No Plan'}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Quota: {((t.token_quota || 0) / 1000000).toFixed(1)}M
                  </div>
                </td>
                <td style={{ padding: '16px', fontWeight: 500, color: 'var(--text-primary)' }}>
                  {((t.tokens_used || 0) / 1000000).toFixed(2)}M / {((t.token_quota || 0) / 1000000).toFixed(1)}M
                </td>
                <td style={{ padding: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <CustomPlanDropdown 
                    currentPlanId={t.plan_id || ''} 
                    onAssign={(planId) => handleAssignPlan(t.id, planId)} 
                  />
                  <button style={{ padding: '6px 12px', borderRadius: '4px', background: 'var(--bg-glass)', color: 'var(--text-primary)', border: '1px solid var(--border)', cursor: 'pointer', fontSize: '12px', transition: 'var(--transition)' }}>
                    Edit
                  </button>
                  <button onClick={() => handleDeleteUser(t.id)} style={{ padding: '6px 12px', borderRadius: '4px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)', cursor: 'pointer', fontSize: '12px', transition: 'var(--transition)' }}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
